import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API } from '../App';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import { toast } from 'sonner';
import AddSerializedComponentDialog from './AddSerializedComponentDialog';
import { STATUS_META, getStatusMeta, formatDuration } from '../lib/serialized';

const STATUS_ORDER = ['new', 'available', 'installed', 'awaiting_rebuild', 'decommissioned'];

/**
 * Serialized components list. Sits behind the "Serialized Parts" tab on the
 * Inventory page — search by serial / name / mfr / model / linked part #.
 * Status filter chips let the user zero in on parts awaiting rebuild.
 */
const SerializedComponentsList = ({ isAdmin, initialQuery = '' }) => {
  const navigate = useNavigate();
  const [components, setComponents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState(initialQuery || '');
  const [statusFilter, setStatusFilter] = useState('all');
  const [machineryById, setMachineryById] = useState({});
  const [pointById, setPointById] = useState({});
  const [showAdd, setShowAdd] = useState(false);

  // Keep the search box in sync when the parent (Inventory page) hands
  // us a fresh initialQuery via URL param (chip on Consumables row).
  useEffect(() => { setQuery(initialQuery || ''); }, [initialQuery]);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [scRes, mRes] = await Promise.all([
        axios.get(`${API}/serialized-components`),
        axios.get(`${API}/machinery`),
      ]);
      setComponents(scRes.data || []);
      const mById = {};
      (mRes.data || []).forEach((m) => { mById[m.id] = m; });
      setMachineryById(mById);

      // Fetch installation points for any machinery that has an occupant. This
      // is small (one API call per uniquely-installed-on machinery) and keeps
      // the row labels honest.
      const occupiedMachineryIds = Array.from(new Set(
        (scRes.data || [])
          .filter((c) => c.current_machinery_id)
          .map((c) => c.current_machinery_id)
      ));
      const points = await Promise.all(
        occupiedMachineryIds.map((mid) =>
          axios.get(`${API}/machinery/${mid}/installation-points`)
            .then((r) => r.data)
            .catch(() => [])
        )
      );
      const pById = {};
      points.flat().forEach((p) => { pById[p.id] = p; });
      setPointById(pById);
    } catch (err) {
      toast.error('Failed to load serialized components');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchAll(); }, []);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return components.filter((c) => {
      if (statusFilter !== 'all' && c.status !== statusFilter) return false;
      if (!q) return true;
      return (
        (c.serial_number || '').toLowerCase().includes(q) ||
        (c.name || '').toLowerCase().includes(q) ||
        (c.mfr_part_number || '').toLowerCase().includes(q) ||
        (c.manufacturer || '').toLowerCase().includes(q) ||
        (c.model || '').toLowerCase().includes(q)
      );
    });
  }, [components, query, statusFilter]);

  const statusCounts = useMemo(() => {
    const counts = { all: components.length };
    STATUS_ORDER.forEach((s) => { counts[s] = 0; });
    components.forEach((c) => { counts[c.status] = (counts[c.status] || 0) + 1; });
    return counts;
  }, [components]);

  return (
    <div className="space-y-4" data-testid="serialized-list-container">
      {/* Search + Add row */}
      <div className="bg-white rounded-lg shadow p-4 space-y-3">
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="flex-1">
            <Input
              data-testid="sc-search-input"
              placeholder="Search MFR part #, serial, name, manufacturer…"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
          </div>
          {isAdmin && (
            <Button onClick={() => setShowAdd(true)} data-testid="sc-add-btn">
              + Add Serialized Part
            </Button>
          )}
        </div>

        {/* Status filter chips */}
        <div className="flex flex-wrap gap-1.5" data-testid="sc-status-chips">
          {[
            { v: 'all', label: `All (${statusCounts.all})`, cls: 'bg-slate-900 text-white' },
            ...STATUS_ORDER.map((s) => ({
              v: s,
              label: `${STATUS_META[s].label} (${statusCounts[s] || 0})`,
              cls: STATUS_META[s].className,
            })),
          ].map((chip) => (
            <button
              key={chip.v}
              type="button"
              onClick={() => setStatusFilter(chip.v)}
              data-testid={`sc-chip-${chip.v}`}
              className={
                'text-xs rounded-full px-2.5 py-1 border ' +
                (statusFilter === chip.v
                  ? (chip.v === 'all' ? 'bg-slate-900 text-white border-slate-900' : chip.cls)
                  : 'bg-white text-slate-700 border-slate-200 hover:border-slate-400')
              }
            >
              {chip.label}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="bg-white rounded-lg shadow p-8 text-center text-slate-500">Loading…</div>
      ) : filtered.length === 0 ? (
        <div className="bg-white rounded-lg shadow p-8 text-center text-slate-500 italic">
          {components.length === 0
            ? 'No serialized parts yet. Add one with "+ Add Serialized Part".'
            : 'No parts match this search or filter.'}
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow divide-y">
          {filtered.map((c) => {
            const meta = getStatusMeta(c.status);
            const machine = c.current_machinery_id ? machineryById[c.current_machinery_id] : null;
            const point = c.current_installation_point_id ? pointById[c.current_installation_point_id] : null;
            const sinceSec = c.current_installed_at
              ? Math.floor((Date.now() - new Date(c.current_installed_at).getTime()) / 1000)
              : 0;
            return (
              <button
                type="button"
                key={c.id}
                onClick={() => navigate(`/inventory/serialized/${c.id}`)}
                className="w-full text-left px-4 py-3 hover:bg-slate-50 flex items-start justify-between gap-3 transition"
                data-testid={`sc-row-${c.id}`}
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-mono text-xs bg-slate-100 rounded px-1.5 py-0.5">
                      {c.serial_number}
                    </span>
                    <span className="font-semibold text-sm text-slate-900">{c.name}</span>
                    <Badge className={meta.className}>{meta.label}</Badge>
                  </div>
                  <div className="text-xs text-slate-600 mt-1">
                    {c.mfr_part_number && (
                      <>MFR PN: <span className="font-mono">{c.mfr_part_number}</span> </>
                    )}
                    {c.manufacturer && <> · {c.manufacturer} </>}
                    {c.model && <> · {c.model} </>}
                  </div>
                  {c.status === 'installed' && machine && (
                    <div className="text-xs text-blue-800 mt-1">
                      Installed on <span className="font-medium">{machine.component}</span>
                      {point && <> @ <span className="font-medium">{point.label}</span></>}
                      {sinceSec > 0 && <> · {formatDuration(sinceSec)} so far</>}
                    </div>
                  )}
                </div>
                <div className="text-xs text-slate-400 shrink-0 hidden sm:block">
                  Open →
                </div>
              </button>
            );
          })}
        </div>
      )}

      <AddSerializedComponentDialog
        open={showAdd}
        onClose={() => setShowAdd(false)}
        onSuccess={() => { fetchAll(); }}
      />
    </div>
  );
};

export default SerializedComponentsList;
